/**
 * Safe localStorage wrapper utilities
 *
 * Provides safe access to localStorage with error handling for:
 * - SSR environments (window is undefined)
 * - Privacy mode browsers (localStorage may throw)
 * - Quota exceeded scenarios
 */

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    window.localStorage.setItem(test, test);
    window.localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely get a string value from localStorage
 *
 * @param key - The storage key
 * @returns The stored value or null if not found/error
 */
export function getItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Safely set a string value in localStorage
 *
 * @param key - The storage key
 * @param value - The value to store
 * @returns true if successful, false if failed
 */
export function setItem(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely remove an item from localStorage
 *
 * @param key - The storage key
 * @returns true if successful, false if failed
 */
export function removeItem(key: string): boolean {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely get and parse JSON from localStorage
 *
 * @param key - The storage key
 * @param defaultValue - Default value if not found or parse error
 * @returns The parsed value or default
 */
export function getJSON<T>(key: string, defaultValue: T): T {
  try {
    const item = window.localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Safely stringify and store JSON in localStorage
 *
 * @param key - The storage key
 * @param value - The value to store
 * @returns true if successful, false if failed
 */
export function setJSON<T>(key: string, value: T): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * Storage keys used throughout the application
 * Centralizes all localStorage keys in one place
 */
export const STORAGE_KEYS = {
  /** Last email used for login */
  LAST_EMAIL: 'lastEmail',
  /** Demo mode flag (dev only) */
  DEMO_MODE: 'discoveryDialogues_demoMode',
  /** Selected microphone device ID */
  SELECTED_MICROPHONE: 'dd.microphoneDeviceId',
  /** Microphone of the last call that completed with the respondent
   *  heard (LUM-82) */
  LAST_GOOD_MICROPHONE: 'dd.lastGoodMicrophoneDeviceId',
  /** Selected speaker device ID */
  SELECTED_SPEAKER: 'dd.speakerDeviceId',
  /** Bluetooth tip dismissed flag (macOS) */
  BLUETOOTH_TIP_DISMISSED: 'dd_mac_bt_tip_dismissed',
} as const;
